import { useContext, useMemo, useState } from 'react';
import CampaignOverViewComponent from './CampaignOverViewComponent';
import ChartAnaylysisComponent from './ChartAnaylysisComponent';
import ValueVsSpendComponent from './BrandValueVsSpendComponent';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import useI18n from '@/hooks/useI18n';
import IconButton from '../IconButton';
import { toast } from 'react-toastify';
import BrandSentimentsComponent from './BrandSentimentsComponent';
import ContentAnaylysisComponent from './ContentAnaylysisComponent';
import ToolTip from '../ToolTip';
import { UserContext } from '../../../context/UserContext';
import {
  useCampaignAnalysisQuery,
  useEarnedMediaValueChartQuery,
  useRoasAnalysisChartQuery,
  ValueVsSpendMetrics,
} from '@/hooks/queries/useAnalyticsQueries';
import Loading from '../Loading';
import { useDateRangeQueryParams } from '@/hooks/useDateRangeQueryParams';
import { DUMMY_VALUE_VS_SPEND_BRAND } from './dummyAnalytics';
const currentHost = window.location.origin;

const CampaignTabComponent = ({
  formattedEndDate,
  formattedStartDate,
  dateFilteredData,
}: {
  formattedEndDate: string;
  formattedStartDate: string;
  dateFilteredData: any;
}) => {
  const { t } = useI18n();
  const [pdfInProgress, setPdfInProgress] = useState(false);
  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user, isDummyUser } = userContext;

  const brandId = user?.brand?._id || '';
  const isRealUser = user?.role === 'brandUser' && !isDummyUser && !!brandId;

  const {
    filter: chartFilter,
    customStartDate,
    customEndDate,
  } = useDateRangeQueryParams(dateFilteredData);

  const { data: campaignAnalysisData, isLoading: queryLoading } =
    useCampaignAnalysisQuery(
      brandId,
      isRealUser,
      chartFilter,
      customStartDate,
      customEndDate,
    );
  const isChartQueryEnabled = isRealUser && !!chartFilter;
  const { data: earnedMediaResponse } = useEarnedMediaValueChartQuery(
    brandId,
    chartFilter,
    isChartQueryEnabled,
    customStartDate,
    customEndDate,
  );
  const { data: roasResponse } = useRoasAnalysisChartQuery(
    brandId,
    chartFilter,
    isChartQueryEnabled,
    customStartDate,
    customEndDate,
  );

  const isLoading = isRealUser ? queryLoading : false;

  const campaignMetrics = isDummyUser
    ? {
        totalCampaigns: 3,
        activeCampaigns: 1,
        completedCampaigns: 1,
        pausedCampaigns: 1,
        scheduledCampaigns: 0,
      }
    : campaignAnalysisData?.campaignMetrics || {
        totalCampaigns: 0,
        activeCampaigns: 0,
        completedCampaigns: 0,
        pausedCampaigns: 0,
        scheduledCampaigns: 0,
      };

  const brandMetrics = campaignAnalysisData?.brandMetrics;

  const dummyChartData = useMemo(() => {
    if (!isDummyUser) return null;
    const labels = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
    const spike = [1.3, 0.85, 1.6, 0.9, 1.9, 1.1];
    // Reconcile chart series with the Value-vs-Spend totals
    // (spend 1,840 · EMV 17,733.54 · sales 4,600; ROAS = 4600/1840 = 2.5).
    const weights = labels.map(
      (_, i) => Math.pow(1.1, i) * spike[i % spike.length],
    );
    const sumW = weights.reduce((s, w) => s + w, 0);
    const distribute = (total: number) =>
      weights.map((w) => Math.round((total * w) / sumW));
    const spendingData = distribute(1840);
    const emvData = distribute(17734);
    const salesData = distribute(4600);
    return {
      labels,
      spendingData,
      emvData,
      salesData,
      totalEmv: emvData.reduce((s, v) => s + v, 0),
      roas: {
        allRoas: [2.3, 2.6, 2.4, 2.7, 2.5, 2.5],
        instagramRoas: [2.6, 2.9, 2.7, 3.0, 2.8, 2.8],
        tiktokRoas: [1.9, 2.2, 2.0, 2.3, 2.1, 2.1],
        averageRoasOfTotal: 2.5,
        averageRoasOfInstagram: 2.8,
        averageRoasOfTiktok: 2.1,
      },
    };
  }, [isDummyUser]);

  const valueVsSpend = useMemo<ValueVsSpendMetrics>(() => {
    if (isDummyUser) return DUMMY_VALUE_VS_SPEND_BRAND;
    if (!brandMetrics) {
      return {
        roi: 0,
        averageRoas: 0,
        totalCampaignValue: '0',
        cgc: '0',
        totalEmv: '0',
        totalSales: '0',
        totalSpent: 0,
        approvedCashbackCount: 0,
      };
    }
    return {
      roi: brandMetrics.roi ?? 0,
      averageRoas: brandMetrics.averageRoas ?? 0,
      totalCampaignValue: brandMetrics.totalBrandValue ?? '0',
      cgc: brandMetrics.cgc ?? '0',
      totalEmv: String(brandMetrics.totalEmv ?? 0),
      totalSales: String(brandMetrics.totalSales ?? 0),
      totalSpent: brandMetrics.totalSpent ?? 0,
      approvedCashbackCount: brandMetrics.approvedCashbackCount ?? 0,
    };
  }, [brandMetrics, isDummyUser]);

  const removeElementsByClass = (className: string) => {
    document.querySelectorAll(`.${className}`).forEach((el) => el.remove());
  };

  const print = async () => {
    const input = document.getElementById('printArea');
    if (!input) return;

    setPdfInProgress(true);

    try {
      removeElementsByClass('apexcharts-ycrosshairs');
      removeElementsByClass('apexcharts-xcrosshairs');

      setTimeout(async () => {
        try {
          const dataUrl = await toPng(input, {
            pixelRatio: 2,
            backgroundColor: '#FFFFFF',
          });

          const img = new window.Image();
          img.src = dataUrl;
          img.onload = () => {
            const pageWidth = 210;
            const marginX = 5;
            const marginY = 12;
            const bottomMargin = 10;
            const logoHeight = 6;
            const logoMargin = 5;

            const pxPerMm = img.width / (pageWidth - 2 * marginX);
            const imgWidthMm = pageWidth - 2 * marginX;
            const imgHeightMm = img.height / pxPerMm;

            const totalContentHeight =
              logoMargin + logoHeight + imgHeightMm + marginY + bottomMargin;

            const pdf = new jsPDF('p', 'mm', [
              pageWidth,
              Math.max(totalContentHeight, 30),
            ]);

            pdf.addImage(
              `${currentHost}/assets/Logo.png`,
              'PNG',
              marginX,
              logoMargin,
              30,
              logoHeight,
              undefined,
              'NONE',
            );

            pdf.addImage(
              dataUrl,
              'PNG',
              marginX,
              logoMargin + logoHeight + marginY,
              imgWidthMm,
              imgHeightMm,
            );

            pdf.save('Analytics_Report.pdf');
            setPdfInProgress(false);
          };
        } catch (error) {
          toast.error(t('common.errorMsg'));
          setPdfInProgress(false);
        }
      }, 2000);
    } catch (error) {
      setPdfInProgress(false);
    }
  };

  const campaignData = [
    { title: t('common.totalCampaign'), value: campaignMetrics.totalCampaigns },
    {
      title: t('common.activeCampaign'),
      value: campaignMetrics.activeCampaigns,
    },
    {
      title: t('common.completedCampaign'),
      value: campaignMetrics.completedCampaigns,
    },
    {
      title: t('common.pausedCampaign'),
      value: campaignMetrics.pausedCampaigns,
    },
    {
      title: t('common.scheduledCampaign'),
      value: campaignMetrics.scheduledCampaigns,
    },
  ];

  return (
    <div
      id="printArea"
      className="print:flex-col max-w-full w-full overflow-hidden"
    >
      {isLoading ? (
        <Loading
          centeredToParent={true}
          style={{
            top: 'calc(50% + 60px)',
          }}
        />
      ) : (
        <div className="flex flex-col gap-6">
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-sm font-medium text-[#121111] opacity-90">
                {t('common.campaignOverview')}
              </h2>
              <div className="flex items-center gap-3">
                {!pdfInProgress && (
                  <ToolTip text={String(t('common.download'))} position="top">
                    <IconButton className="w-[24px] h-[24px]">
                      <img onClick={print} src="/assets/download.svg" />
                    </IconButton>
                  </ToolTip>
                )}
                {pdfInProgress && (
                  <>
                    {formattedStartDate} -{formattedEndDate}
                  </>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {campaignData.map((item, index) => (
                <div
                  key={index}
                  className="bg-white p-[24px] border border-[#E5E9EB] rounded-3xl flex flex-col gap-1"
                  style={{ boxShadow: '0px 21px 94px 0px rgba(0, 0, 0, 0.03)' }}
                >
                  <p className="text-xs text-[#9B919D]">{item.title}</p>
                  <p className="text-2xl font-medium text-[#292828]">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <ValueVsSpendComponent metrics={valueVsSpend} />
          <CampaignOverViewComponent brandMetrics={brandMetrics} />
          <ChartAnaylysisComponent
            earnedMedia={
              isDummyUser
                ? {
                    labels: dummyChartData?.labels,
                    emvData: dummyChartData?.emvData,
                    salesData: dummyChartData?.salesData,
                    spendingData: dummyChartData?.spendingData,
                    totalEmv: dummyChartData?.totalEmv,
                  }
                : earnedMediaResponse
            }
            roas={
              isDummyUser
                ? {
                    labels: dummyChartData?.labels,
                    allRoas: dummyChartData?.roas.allRoas,
                    instagramRoas: dummyChartData?.roas.instagramRoas,
                    tiktokRoas: dummyChartData?.roas.tiktokRoas,
                    averageRoasOfTotal: dummyChartData?.roas.averageRoasOfTotal,
                    averageRoasOfInstagram:
                      dummyChartData?.roas.averageRoasOfInstagram,
                    averageRoasOfTiktok:
                      dummyChartData?.roas.averageRoasOfTiktok,
                  }
                : roasResponse
            }
          />
          <ContentAnaylysisComponent
            chartFilter={chartFilter}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
          />
          <BrandSentimentsComponent
            chartFilter={chartFilter}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
          />
        </div>
      )}
    </div>
  );
};

export default CampaignTabComponent;
